import React, { useState, useEffect } from "react";
import { Container, Box, Typography } from "@mui/material";
import liff from "../../../lib/liff";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import QuestCard from "default/components/QuestCard";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { listSystemQuests } from "utils/api";
import Loading from "default/components/Loading";
import ImageWithSaveButton from "default/components/ImageWithSaveButton";
import StyledButton from "default/components/StyledButton";
import Error from "default/components/Error";
import TicketNotFound from "default/components/TicketNotFound";
import { useUrlParams } from "hooks/useUrlParams";
import PotionLayout from "../components/PotionLayout";
import { usePotionTicketState } from "../hooks/usePotionTicketState";

// Helper functions
const withLng = (path, lng) => {
  const hasQuery = path.includes("?");
  return lng ? `${path}${hasQuery ? "&" : "?"}lng=${lng}` : path;
};

// Custom hook for quest data management (uses new ticket state check + existing quest group API)
const usePotionQuestGroups = (soulId) => {
  const { hasPurchased, isLoading: ticketLoading, isError: ticketError } =
    usePotionTicketState(soulId);

  const [questGroups, setQuestGroups] = useState([]);
  const [isLoading, setIsLoading] = useState(Boolean(soulId));
  const [isError, setIsError] = useState(false);

  // チケット状態の判定を待たずに投げる。クエスト API はチケットを前提にしていないので、
  // 直列にすると往復が1回まるごと余計にかかる（モバイル回線では体感に効く）。
  useEffect(() => {
    let active = true;

    if (!soulId) {
      setIsLoading(false);
      return () => { active = false; };
    }

    const fetchQuests = async () => {
      setIsLoading(true);
      setIsError(false);
      try {
        const { data } = await listSystemQuests(soulId);
        if (active) setQuestGroups(data);
      } catch (error) {
        if (active) setIsError(true);
      } finally {
        if (active) setIsLoading(false);
      }
    };

    fetchQuests();
    return () => { active = false; };
  }, [soulId]);

  return {
    questGroups,
    isLoading: ticketLoading || isLoading,
    hasPurchased,
    // 未購入なら TicketNotFound を出すのが正しいので、クエスト側の失敗は伏せる。
    isError: ticketError || (hasPurchased && isError),
  };
};

// Component for empty quests state
const EmptyQuestsView = ({ t }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      textAlign: "center",
      minHeight: "100vh",
    }}
  >
    <SearchOffIcon sx={{ fontSize: "100px", color: "text.disabled" }} />
    <Typography
      variant="h6"
      sx={{
        marginTop: "16px",
        marginBottom: "16px",
        color: "text.secondary",
        fontSize: "16px",
      }}
    >
      {t("common:quest.no_quests")}
    </Typography>
  </Box>
);

// 見出しは先頭1文字だけをテーマ色で強調する（"Reward" / "Event Quest" 共通）
const SectionHeader = ({ label, sx = {} }) => (
  <Typography
    sx={(theme) => ({
      ...theme.components.sectionHeader,
      ...sx,
    })}
  >
    <span style={{ color: "#8CB808" }}>{label.charAt(0)}</span>
    {label.slice(1)}
  </Typography>
);

// 報酬を持つグループは "Reward"、報酬のないイベント枠はグループ名を見出しにする
const getGroupTitle = (group) =>
  group.quest_group_name || (group.reward_url ? "Reward" : "Quest");

// Component for quest content
const QuestContent = ({
  questGroups,
  lng,
  t,
  onRequestJudgement,
  onCloseWindow,
}) => (
  <Box
    sx={{
      display: "flex",
      paddingTop: 4,
      paddingBottom: 4,
      flexDirection: "column",
      alignItems: "center",
      gap: 3,
      minHeight: "100vh",
    }}
  >
    {questGroups.map((group, index) => {
      return (
        <React.Fragment key={group.quest_group_key}>
          <SectionHeader label={getGroupTitle(group)} />

          <Box key={group.quest_group_key} sx={{ width: "100%" }}>
            {group.reward_url && (
              <Container
                sx={{
                  display: "flex",
                  paddingLeft: 0,
                  paddingRight: 0,
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 2,
                  width: "100%",
                  marginBottom: 3,
                }}
              >
                <ImageWithSaveButton
                  imageUrls={
                    group.quests.every((quest) => quest.is_cleared)
                      ? [group.reward_url]
                      : group.quests
                          .filter(
                            (quest) => quest.is_cleared && quest.reward_url
                          )
                          .map((quest) => quest.reward_url)
                  }
                  showDownloadButton={group.quests.every(
                    (quest) => quest.is_cleared
                  )}
                />
              </Container>
            )}

            {group.reward_url && <SectionHeader label="Quest" sx={{ mb: 2 }} />}

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
              }}
            >
              {group.quests.map((quest) => (
                <QuestCard
                  key={quest.quest_key}
                  quest={quest}
                  style={{ cursor: "pointer" }}
                />
              ))}
            </Box>
          </Box>

          {index < questGroups.length - 1 && (
            <Box
              sx={(theme) => ({
                width: "100%",
                height: "1px",
                marginTop: 2,
                marginBottom: 2,
                backgroundColor: theme.components.divider.backgroundColor,
              })}
            />
          )}
        </React.Fragment>
      );
    })}

    {lng === "ja" && (
      <StyledButton
        variant="contained"
        sx={{ marginTop: 4 }}
        onClick={onRequestJudgement}
      >
        {t("common:button.request_clear_judgement")}
      </StyledButton>
    )}

    <StyledButton
      variant="contained"
      sx={{ marginTop: 1 }}
      onClick={onCloseWindow}
    >
      {t("common:button.back_to_chat")}
    </StyledButton>
  </Box>
);

export default function Quests() {
  const navigate = useNavigate();
  const { soulId, lng, work } = useUrlParams();
  const { t } = useTranslation(["common"]);

  const { questGroups, isLoading, hasPurchased, isError } =
    usePotionQuestGroups(soulId);

  const handleNavigateToTickets = () => {
    navigate(withLng(`/${work}/tickets?soul_id=${soulId}`, lng));
  };

  const handleCloseWindow = () => {
    liff.closeWindow();
  };

  const handleRequestJudgement = () => {
    window.open(
      "https://docs.google.com/forms/d/e/1FAIpQLSckxdz-E1hLeEAwtCSYx5zoSNq6s-tDAibm7uEu3xMNDhMI8g/viewform",
      "_blank"
    );
  };

  if (isLoading) {
    return (
      <PotionLayout>
        <Loading />
      </PotionLayout>
    );
  }

  if (isError) {
    return (
      <PotionLayout>
        <Error />
      </PotionLayout>
    );
  }

  if (!hasPurchased) {
    return (
      <PotionLayout>
        <TicketNotFound onClick={handleNavigateToTickets} />
      </PotionLayout>
    );
  }

  if (questGroups.length === 0) {
    return (
      <PotionLayout>
        <EmptyQuestsView t={t} />
      </PotionLayout>
    );
  }

  return (
    <PotionLayout>
      <QuestContent
        questGroups={questGroups}
        lng={lng}
        t={t}
        onRequestJudgement={handleRequestJudgement}
        onCloseWindow={handleCloseWindow}
      />
    </PotionLayout>
  );
}
